// Diaspora customer dashboard. Wired to live /recipients + /requests
// endpoints. Stat tiles now derive from real data — no DemoDataBadge
// where the numbers are honest.

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Users, Package, CreditCard, Repeat, Plus, Wallet, ArrowRight,
  CheckCircle2, MessageCircle, ShoppingBag, MapPin, Phone, History,
  LayoutDashboard, FileText, Settings, Loader2, Gift,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { PortalShell, type NavSection } from '../components/shared/PortalShell';
import {
  Card, SectionHeader, StatTile, ActivityRow, EmptyState,
  StatusPill, DemoDataBadge,
} from '../components/shared/primitives';
import { recipients, requests, type Recipient, type ServiceRequestWithItems } from '../lib/api';
import { formatHtgMinor, formatUsdFromHtgMinor } from '../lib/money';
import { airtimeEnabled } from '../lib/featureFlags';
import { Smartphone } from 'lucide-react';

const CUSTOMER_NAV: NavSection[] = [
  { items: [
    { id: 'dashboard',  label: 'Dashboard',     icon: LayoutDashboard, href: '/customer' },
    { id: 'requests',   label: 'Requests',      icon: Package,         href: '/customer/requests' },
    // Airtime (Voye send-product) — shown only when the feature flag is on, so
    // the live portal is unchanged by default (matches the flag-gated route).
    ...(airtimeEnabled()
      ? [{ id: 'airtime', label: 'Airtime', icon: Smartphone, href: '/customer/airtime' } as const]
      : []),
    { id: 'packages',   label: 'Care packages', icon: Gift,            href: '/customer/packages' },
    { id: 'recipients', label: 'Recipients',    icon: Users,           href: '/customer/recipients' },
  ]},
  { title: 'Payments', items: [
    { id: 'methods',    label: 'Payment methods', icon: CreditCard, href: '/customer/payment-methods' },
    { id: 'history',    label: 'History',         icon: History,    href: '/customer/history' },
    { id: 'recurring',  label: 'Recurring',       icon: Repeat,     href: '/customer/recurring' },
  ]},
  { title: 'Account', items: [
    { id: 'settings',   label: 'Settings',        icon: Settings,   href: '/customer/settings' },
  ]},
];

interface CustomerProfile { full_name?: string; country?: string }

const ACTIVE_STATUSES = new Set([
  'submitted', 'quote_pending', 'quoted', 'customer_approval_pending',
  'payment_pending', 'paid', 'assigned_to_merchant', 'in_progress', 'ready_for_delivery', 'delivered',
]);

export function CustomerDashboard() {
  const { user } = useAuth();
  const cp = (user?.customer_profile ?? {}) as CustomerProfile;
  const firstName = (cp.full_name ?? user?.email ?? 'there').split(' ')[0];

  const recipQ    = useQuery({ queryKey: ['me', 'recipients'], queryFn: recipients.list });
  const requestsQ = useQuery({ queryKey: ['me', 'requests'],   queryFn: () => requests.list() });

  const stats = useMemo(() => {
    const recipCount = recipQ.data?.length ?? 0;
    const reqs       = requestsQ.data ?? [];
    const active     = reqs.filter((r) => ACTIVE_STATUSES.has(r.status));
    const confirmed  = reqs.filter((r) => r.status === 'confirmed');
    // Quoted/approved cart values sum to "what's in flight". Confirmed
    // sum to "delivered". Real "spent this month" comes via /payments
    // when Phase 2 lands; for now we sum confirmed cart subtotals as
    // an approximation (USD via approx FX).
    const inFlightHtg  = active.reduce((a, r) => a + (r.cart_subtotal_htg_minor ?? 0), 0);
    const confirmedHtg = confirmed.reduce((a, r) => a + (r.cart_subtotal_htg_minor ?? 0), 0);
    return { recipCount, activeCount: active.length, confirmedCount: confirmed.length, inFlightHtg, confirmedHtg };
  }, [recipQ.data, requestsQ.data]);

  return (
    <PortalShell
      nav={CUSTOMER_NAV}
      activeId="dashboard"
      title="Home"
      subtitle="Your family support, at a glance."
      actions={
        <Link
          to="/customer/requests/new"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-brand-600 text-white text-[12px] font-semibold hover:bg-brand-700"
        >
          <Plus className="w-3.5 h-3.5" /> New request
        </Link>
      }
    >
      <div className="space-y-6">
        {/* Voye hero — the mobile home's signature: deep-blue gradient, a 50/50
            Haiti flag stripe (the only flag reference), and 2-up product cards.
            Ported so the web reads as the same product as the app. */}
        <VoyeHero firstName={firstName} />

        {/* Hero stats — real numbers from live queries. */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatTile icon={Users}      tone="brand"   label="Family supported"   value={String(stats.recipCount)}    hint="Recipients in HT" />
          <StatTile icon={Package}    tone="amber"   label="Active requests"    value={String(stats.activeCount)}   hint={stats.activeCount === 0 ? 'None in flight' : 'Awaiting quote or delivery'} />
          <StatTile icon={Wallet}     tone="emerald" label="In-flight cart value" value={stats.inFlightHtg ? formatHtgMinor(stats.inFlightHtg) : '—'} hint={stats.inFlightHtg ? `≈ ${formatUsdFromHtgMinor(stats.inFlightHtg)}` : 'Add items to a request'} />
          <StatTile icon={CheckCircle2} tone="default" label="Delivered"        value={String(stats.confirmedCount)} hint={stats.confirmedHtg ? `Confirmed ≈ ${formatUsdFromHtgMinor(stats.confirmedHtg)}` : 'All time'} />
        </div>

        {/* Two-column area: recipients + activity */}
        <div className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-5">
            <Card>
              <SectionHeader
                title="Your recipients"
                subtitle="Family members you support in Haiti."
                actions={
                  <Link to="/customer/recipients" className="text-[12px] font-semibold text-brand-600 hover:underline inline-flex items-center gap-0.5">
                    Manage <ArrowRight className="w-3 h-3" />
                  </Link>
                }
              />
              <RecipientsBlock data={recipQ.data} loading={recipQ.isLoading} />
            </Card>

            <Card>
              <SectionHeader
                title="Active requests"
                subtitle="Anything not yet confirmed by your recipient."
                actions={<Link to="/customer/requests" className="text-[12px] font-semibold text-brand-600 hover:underline inline-flex items-center gap-0.5">View all <ArrowRight className="w-3 h-3" /></Link>}
              />
              <RequestsBlock data={requestsQ.data} loading={requestsQ.isLoading} />
            </Card>
          </div>

          <div className="space-y-5">
            <Card>
              <SectionHeader title="Recent activity" />
              <ActivityBlock requests={requestsQ.data ?? []} />
            </Card>

            <Card padding="p-4" className="bg-gradient-to-br from-brand-50 to-amber-50/40 border-brand-100">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-brand-700">Tip</p>
              <p className="mt-1 text-[13px] font-semibold text-slate-900">Set up a recurring support plan</p>
              <p className="mt-1 text-[12px] text-slate-600">Send Maria $50 of groceries every two weeks — automatically, on the same day.</p>
              <Link to="/customer/recurring" className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold text-brand-700 hover:underline">
                <ShoppingBag className="w-3.5 h-3.5" /> Plan recurring support →
              </Link>
            </Card>
          </div>
        </div>
      </div>
    </PortalShell>
  );
}

// ── Voye hero (ported from the mobile home) ────────────────

function VoyeHero({ firstName }: { firstName: string }) {
  return (
    <section
      className="relative overflow-hidden rounded-[var(--radius-hero)] p-6 text-white"
      style={{ background: 'linear-gradient(160deg,#063B9E,#0A2D75)', boxShadow: 'var(--shadow-card)' }}
    >
      {/* 50/50 Haiti flag stripe — the only flag reference. */}
      <div aria-hidden className="absolute inset-x-0 top-0 flex h-1">
        <span className="w-1/2 bg-[var(--color-interactive)]" />
        <span className="w-1/2 bg-[var(--color-haiti-red)]" />
      </div>
      <p className="text-[11.5px] font-bold uppercase tracking-[1.2px] text-[#9FBBF5]">Ayiti · +509</p>
      <h1 className="mt-2 text-[26px] font-extrabold leading-[1.15] tracking-[-0.7px]">
        {firstName ? `Bonjou, ${firstName}` : 'Send something home today'}
      </h1>

      <div className="mt-5 grid grid-cols-2 gap-[10px] max-w-md">
        <HeroActionCard
          to="/customer/requests/new"
          title="Send groceries"
          sub="From a local merchant"
          tag="GROCERIES"
        />
        {airtimeEnabled() ? (
          <HeroActionCard
            to="/customer/airtime"
            title="Send airtime"
            sub="Top up a phone"
            tag="DIGICEL · NATCOM"
          />
        ) : (
          <HeroActionCard to="/customer/packages" title="Care packages" sub="Ready-made baskets" tag="POPULAR" />
        )}
      </div>
    </section>
  );
}

function HeroActionCard({ to, title, sub, tag }: { to: string; title: string; sub: string; tag: string }) {
  return (
    <Link
      to={to}
      className="flex min-h-[96px] flex-col justify-between rounded-[var(--radius-button)] bg-white p-4 text-left transition-colors hover:bg-[#EEF3FF]"
    >
      <span className="text-[14px] font-extrabold text-[var(--color-ink)]">{title}</span>
      <span>
        <span className="block text-[11.5px] text-[var(--color-secondary)]">{sub}</span>
        <span className="mt-1 block text-[9.5px] font-extrabold tracking-wide text-[var(--color-muted)]">{tag}</span>
      </span>
    </Link>
  );
}

// ── Recipients block ───────────────────────────────────────

function RecipientsBlock({ data, loading }: { data: Recipient[] | undefined; loading: boolean }) {
  if (loading) return <Loading />;
  const list = data ?? [];
  if (list.length === 0) {
    return <EmptyState
      icon={Users}
      title="No recipients yet"
      body="Add a family member so they can submit requests."
      action={<Link to="/customer/recipients" className="text-[12px] font-semibold text-brand-600 hover:underline">Add a recipient →</Link>}
    />;
  }
  return (
    <div className="divide-y divide-slate-100 -mx-1">
      {list.map((r) => (
        <div key={r.id} className="px-1 py-3 flex items-center gap-3">
          <span className="inline-flex w-10 h-10 items-center justify-center rounded-full bg-rose-50 text-rose-700 text-[12px] font-semibold">
            {initialsOf(r.full_name)}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-slate-900 truncate">{r.full_name}</p>
            <p className="text-[11.5px] text-slate-500 inline-flex items-center gap-2 truncate">
              <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.city?.name ?? '—'}</span>
              <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {r.phone_e164}</span>
              {r.relationship && <span className="capitalize text-slate-400">· {r.relationship}</span>}
            </p>
          </div>
          <Link to="/customer/requests/new" className="text-[11.5px] text-brand-600 font-semibold hover:underline shrink-0">
            Buy for them →
          </Link>
        </div>
      ))}
    </div>
  );
}

// ── Requests block ─────────────────────────────────────────

function RequestsBlock({ data, loading }: { data: ServiceRequestWithItems[] | undefined; loading: boolean }) {
  if (loading) return <Loading />;
  const active = (data ?? []).filter((r) => ACTIVE_STATUSES.has(r.status));
  if (active.length === 0) {
    return <EmptyState
      icon={Package}
      title="No active requests"
      body="Build a cart from a local merchant's inventory and your family will see it instantly."
      action={<Link to="/customer/requests/new" className="inline-flex items-center gap-1 text-[12px] font-semibold text-brand-600 hover:underline">New request →</Link>}
    />;
  }
  return (
    <table className="text-[12.5px] w-full -mt-1 block overflow-x-auto whitespace-nowrap sm:table sm:whitespace-normal">
      <thead className="text-[10.5px] uppercase tracking-wider text-slate-400">
        <tr>
          <th className="text-left py-2 font-medium">Request</th>
          <th className="text-left py-2 font-medium">For</th>
          <th className="text-left py-2 font-medium">Status</th>
          <th className="text-left py-2 font-medium">Items</th>
          <th className="text-right py-2 font-medium">HTG</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {active.map((r) => (
          <tr key={r.id}>
            <td className="py-2.5">
              <span className="font-mono text-cyan-700">REQ-{String(r.id).padStart(4, '0')}</span>
              <span className="text-slate-600"> · {r.category?.label ?? r.category_id}</span>
            </td>
            <td className="py-2.5">{r.recipient?.full_name?.split(' ')[0] ?? '—'}</td>
            <td className="py-2.5"><StatusPill status={r.status} /></td>
            <td className="py-2.5 text-slate-600">{r.cart_item_count > 0 ? `${r.cart_item_count} item${r.cart_item_count === 1 ? '' : 's'}` : '—'}</td>
            <td className="py-2.5 text-right font-mono">{r.cart_subtotal_htg_minor > 0 ? formatHtgMinor(r.cart_subtotal_htg_minor, { withSymbol: false }) : <span className="text-slate-400">—</span>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Activity block (derived from requests for now) ─────────

function ActivityBlock({ requests }: { requests: ServiceRequestWithItems[] }) {
  // Real audit feed lands when /admin/audit-logs is exposed to the
  // customer (Phase 2). For now derive a friendly feed from the
  // 5 most-recently-updated requests.
  const recent = [...requests]
    .sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? ''))
    .slice(0, 5);

  if (recent.length === 0) {
    return (
      <div className="text-center py-6 text-[12.5px] text-slate-500">
        Nothing yet. Start with a new request.
        <p className="mt-2 inline-block"><DemoDataBadge note="activity populates once you submit your first request" /></p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-slate-100 -mx-1 px-1">
      {recent.map((r) => (
        <ActivityRow
          key={r.id}
          icon={iconForStatus(r.status)}
          iconTone={toneForStatus(r.status)}
          title={
            <>
              <strong className="font-semibold">REQ-{String(r.id).padStart(4, '0')}</strong>
              {' · '}
              <span className="capitalize">{r.status.replace(/_/g, ' ')}</span>
            </>
          }
          meta={<>{r.category?.label ?? ''} · {r.recipient?.full_name?.split(' ')[0] ?? ''}{r.cart_item_count > 0 ? ` · ${r.cart_item_count} items` : ''}</>}
          time={relativeTime(r.updated_at)}
        />
      ))}
    </div>
  );
}

function iconForStatus(s: string) {
  if (s === 'confirmed') return CheckCircle2;
  if (s === 'quoted' || s === 'customer_approval_pending') return Package;
  if (s === 'paid' || s === 'in_progress' || s === 'ready_for_delivery' || s === 'delivered') return ShoppingBag;
  return MessageCircle;
}
function toneForStatus(s: string): 'emerald' | 'brand' | 'amber' | 'slate' | 'rose' {
  if (s === 'confirmed' || s === 'paid' || s === 'delivered') return 'emerald';
  if (s === 'quoted' || s === 'in_progress' || s === 'ready_for_delivery') return 'brand';
  if (s === 'cancelled' || s === 'refunded' || s === 'failed') return 'rose';
  if (s === 'submitted' || s === 'quote_pending') return 'amber';
  return 'slate';
}

function relativeTime(iso: string | undefined): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  const now = Date.now();
  const sec = Math.max(0, Math.floor((now - t) / 1000));
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60); if (min < 60) return `${min}m ago`;
  const hr  = Math.floor(min / 60); if (hr  < 24) return `${hr}h ago`;
  const d   = Math.floor(hr / 24);  if (d   < 7)  return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function Loading() {
  return (
    <div className="text-center py-8 text-[12px] text-slate-400">
      <Loader2 className="inline w-4 h-4 animate-spin" /> Loading…
    </div>
  );
}

function initialsOf(name: string): string {
  return name.split(' ').filter(Boolean).slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('');
}

// FileText was imported but only used below for a tiny demo note —
// dropped here when the panel goes fully real-data. Re-add when we
// surface the underlying audit log.
void FileText;
